/**
 * Pure helpers for HVAC duct rendering (2D plan + 3D viewer): centerline →
 * straight-segment transforms, orientation frames for extruded geometry,
 * supply/return color coding, stroke widths proportional to duct size, and
 * the z-based storey assignment that keeps ducts in sync with per-storey
 * isolation (the duct DTO carries no storey id — terminals do).
 *
 * Deterministic, no IO, no randomness, no three.js dependency — everything
 * here is plain math so it unit-tests as pure functions.
 */
import type { DuctSystem, MepDuct, MepFitting, MepTerminal } from "../api/types";
import { flipYPoints } from "../util/coordinate";

export type Vec3 = [number, number, number];

/**
 * Supply/return color coding (2D strokes, 3D materials, legend). Blue for
 * supply, red for return — the SMACNA / standard MEP-drawing convention.
 */
export const DUCT_COLORS: Record<DuctSystem, string> = {
  supply: "#4a90d9",
  return: "#ef4444",
};

export function ductColor(system: DuctSystem): string {
  return DUCT_COLORS[system];
}

/** Characteristic plan width of a duct (meters): Ø for round, width for rect. */
export function ductPlanWidth(duct: Pick<MepDuct, "shape" | "diameter_m" | "width_m">): number {
  const w = duct.shape === "round" ? duct.diameter_m : duct.width_m;
  return w !== null && w > 0 ? w : 0.15;
}

/** 2D stroke width ∝ duct size, clamped so hairline ducts stay clickable. */
export function ductStrokeWidth(duct: Pick<MepDuct, "shape" | "diameter_m" | "width_m">): number {
  return Math.min(1.2, Math.max(0.12, ductPlanWidth(duct)));
}

/** "Ø 0.30 m" (round) or "0.40 × 0.25 m" (rect); falls back to "—". */
export function ductSizeLabel(
  duct: Pick<MepDuct, "shape" | "diameter_m" | "width_m" | "height_m">,
): string {
  if (duct.shape === "round") {
    return duct.diameter_m !== null ? `Ø ${duct.diameter_m.toFixed(2)} m` : "—";
  }
  if (duct.width_m !== null && duct.height_m !== null) {
    return `${duct.width_m.toFixed(2)} × ${duct.height_m.toFixed(2)} m`;
  }
  return "—";
}

/** SVG `points` for the plan polyline (same y-flip as wizard/plan svgPoints). */
export function ductPolylinePoints(centerline: readonly Vec3[]): string {
  return flipYPoints(centerline);
}

// ---------------------------------------------------------------------------
// Centerline → straight-segment transforms (3D extrusion)
// ---------------------------------------------------------------------------

/** One straight run between consecutive centerline points (plan coords). */
export interface DuctSegment {
  start: Vec3;
  end: Vec3;
  /** Segment midpoint — where the extruded primitive is placed. */
  mid: Vec3;
  /** Run length, meters. */
  length: number;
  /** Unit direction start → end. */
  dir: Vec3;
}

const EPSILON = 1e-9;

/**
 * Split a centerline polyline into straight segments with the transform data
 * an extruded primitive needs (midpoint, length, unit direction). Zero-length
 * spans are dropped. Coordinates stay in plan space (x east, y north, z up).
 */
export function segmentTransforms(centerline: readonly Vec3[]): DuctSegment[] {
  const segments: DuctSegment[] = [];
  for (let i = 0; i + 1 < centerline.length; i += 1) {
    const start = centerline[i];
    const end = centerline[i + 1];
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const dz = end[2] - start[2];
    const length = Math.hypot(dx, dy, dz);
    if (length < EPSILON) continue;
    segments.push({
      start: [...start],
      end: [...end],
      mid: [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2, (start[2] + end[2]) / 2],
      length,
      dir: [dx / length, dy / length, dz / length],
    });
  }
  return segments;
}

/** Total run length of a centerline, meters. */
export function ductLength(centerline: readonly Vec3[]): number {
  return segmentTransforms(centerline).reduce((sum, s) => sum + s.length, 0);
}

/**
 * Plan coords (x east, y north, z up) → three.js viewer coords (x, y up, z).
 *
 * Plan north maps to three.js +z, matching how the Fragments importer places
 * the building mesh (verified empirically against the NBU clinic: per-space
 * Fragments bounding boxes line up with footprint centroids under this
 * mapping with a constant per-model offset; the previous z = -y mapping
 * scattered by ~22 m, i.e. it mirrored the overlay). The remaining constant
 * per-model offset is recovered by `computeOverlayOffset` (viewer/align.ts).
 */
export function planToThree([x, y, z]: Vec3): Vec3 {
  return [x, z, y];
}

/**
 * Orthonormal frame for a duct run in three.js coordinates: `forward` along
 * the run, `up` as close to world-up as possible (so rect ducts keep their
 * width horizontal and height vertical), `right` completing the basis.
 * Vertical risers degenerate to a fixed `right = +x` (deterministic).
 */
export function segmentFrame(forward: Vec3): { right: Vec3; up: Vec3; forward: Vec3 } {
  const f = normalize(forward);
  const worldUp: Vec3 = [0, 1, 0];
  let right = cross(worldUp, f);
  if (norm(right) < 1e-6) {
    // Vertical run (f ≈ ±world-up): any horizontal right works; +x for determinism.
    right = [1, 0, 0];
  }
  right = normalize(right);
  const up = normalize(cross(f, right));
  return { right, up, forward: f };
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function norm(v: Vec3): number {
  return Math.hypot(v[0], v[1], v[2]);
}

function normalize(v: Vec3): Vec3 {
  const n = norm(v);
  return n < EPSILON ? [0, 1, 0] : [v[0] / n, v[1] / n, v[2] / n];
}

// ---------------------------------------------------------------------------
// Storey assignment (ducts/fittings carry no storey id — terminals do)
// ---------------------------------------------------------------------------

/**
 * Representative elevation per storey: the median terminal z of each
 * storey's terminals (deterministic; robust against one odd drop).
 */
export function storeyZLevels(
  terminals: readonly Pick<MepTerminal, "storey_id" | "z_m">[],
): Map<string, number> {
  const zs = new Map<string, number[]>();
  for (const t of terminals) {
    if (t.storey_id === null) continue;
    const list = zs.get(t.storey_id) ?? [];
    list.push(t.z_m);
    zs.set(t.storey_id, list);
  }
  const levels = new Map<string, number>();
  // Sorted keys → deterministic iteration.
  for (const key of [...zs.keys()].sort()) {
    const sorted = zs.get(key)!.slice().sort((a, b) => a - b);
    levels.set(key, sorted[Math.floor((sorted.length - 1) / 2)]);
  }
  return levels;
}

/** Storey whose level is nearest to `z` (ties → lexicographically first id). */
export function nearestStoreyForZ(levels: ReadonlyMap<string, number>, z: number): string | null {
  let best: string | null = null;
  let bestDist = Infinity;
  for (const key of [...levels.keys()].sort()) {
    const dist = Math.abs(levels.get(key)! - z);
    if (dist < bestDist) {
      best = key;
      bestDist = dist;
    }
  }
  return best;
}

/** Lowest z on a duct centerline — branch drops reach toward their storey's ceiling. */
function ductRepresentativeZ(duct: MepDuct): number {
  return Math.min(...duct.centerline.map((p) => p[2]));
}

/**
 * duct id → storey id. Ducts now carry their route's `storey_id` directly;
 * the z-inference (nearest terminal elevation to the duct's lowest
 * centerline point) remains as the fallback for systems persisted before
 * the field existed. Null when neither source resolves (no filter basis →
 * ducts stay visible rather than vanish).
 */
export function assignDuctStoreys(
  ducts: readonly MepDuct[],
  terminals: readonly MepTerminal[],
): Map<string, string | null> {
  const levels = storeyZLevels(terminals);
  const assignment = new Map<string, string | null>();
  for (const duct of ducts) {
    assignment.set(
      duct.id,
      duct.storey_id ??
        (levels.size === 0 ? null : nearestStoreyForZ(levels, ductRepresentativeZ(duct))),
    );
  }
  return assignment;
}

/**
 * fitting id → storey id: follow the fitting's first duct (sorted, so the
 * choice is deterministic), falling back to nearest-z when it joins no known
 * duct.
 */
export function assignFittingStoreys(
  fittings: readonly MepFitting[],
  ductStoreys: ReadonlyMap<string, string | null>,
  terminals: readonly MepTerminal[],
): Map<string, string | null> {
  const levels = storeyZLevels(terminals);
  const assignment = new Map<string, string | null>();
  for (const fitting of fittings) {
    const viaDuct = [...fitting.duct_ids]
      .sort()
      .map((id) => ductStoreys.get(id))
      .find((s) => s !== undefined && s !== null);
    assignment.set(
      fitting.id,
      viaDuct ?? (levels.size === 0 ? null : nearestStoreyForZ(levels, fitting.z_m)),
    );
  }
  return assignment;
}

// ---------------------------------------------------------------------------
// Terminal + fitting presentation
// ---------------------------------------------------------------------------

/** Supply terminals render as flat squares, returns as lined squares. */
export function terminalSystem(kind: string): DuctSystem {
  return kind.startsWith("return") || kind.includes("grille") ? "return" : "supply";
}

export function terminalColor(kind: string): string {
  return DUCT_COLORS[terminalSystem(kind)];
}

/** Fitting kinds in the pinned contract; dampers get the alert tint. */
export const FITTING_KINDS = [
  "elbow",
  "tee",
  "reducer",
  "boot",
  "fire_damper",
  "smoke_damper",
  "combination_damper",
] as const;

const FITTING_COLORS: Record<string, string> = {
  elbow: "#8e8eb8",
  tee: "#8e8eb8",
  reducer: "#56b4d3",
  boot: "#98c379",
  fire_damper: "#e06c75",
  smoke_damper: "#e5c07b",
  combination_damper: "#e0823c",
};

export function fittingColor(kind: string): string {
  return FITTING_COLORS[kind] ?? "#aab2c0";
}
